import { useState } from "react";
import { Mail, MapPin, Instagram, Clock, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Overline } from "@/components/common/ui";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("Message sent, we'll be in touch soon!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#FFFCFA]">
      <div className="mx-auto grid max-w-[1400px] gap-16 px-6 py-20 md:grid-cols-2 md:px-12 md:py-28">
        <div>
          <Overline>Help Centre</Overline>
          <h1 className="mt-4 font-display text-5xl font-light leading-tight text-[#2C2B2C] sm:text-6xl">Let's talk glow.</h1>
          <p className="mt-5 max-w-md text-base font-light leading-relaxed text-[#8A7F86]">
            Looking for support? Our friendly Gold Coast team is here to help, and we genuinely love hearing from our community.
          </p>
          <div className="mt-10 space-y-6">
            {[
              [Mail, "Email us", "hello@auraglowwellness.com"],
              [MapPin, "Based in", "Gold Coast, Australia"],
              [Clock, "Response time", "Within 1 business day"],
              [Instagram, "Follow along", "@auraglowwellness"],
            ].map(([Icon, t, d]) => (
              <div key={t} className="flex items-start gap-4">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-[2rem] border border-[#F2E8E6] bg-white"><Icon className="h-5 w-5 text-[#FBC4A8]" /></span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#8A7F86]">{t}</p>
                  <p className="text-base text-[#2C2B2C]">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="rounded-[2rem] border border-[#F2E8E6] bg-white p-8 md:p-10">
          <h2 className="font-display text-2xl text-[#2C2B2C]">Send a message</h2>
          <div className="mt-6 space-y-4">
            <input data-testid="contact-name" required placeholder="Your name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-[2rem] border border-[#F2E8E6] bg-[#FFFCFA] px-4 py-3.5 text-sm focus:border-[#2C2B2C] focus:outline-none" />
            <input data-testid="contact-email" required type="email" placeholder="Email address" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-[2rem] border border-[#F2E8E6] bg-[#FFFCFA] px-4 py-3.5 text-sm focus:border-[#2C2B2C] focus:outline-none" />
            <textarea data-testid="contact-message" required rows={5} placeholder="How can we help?" value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full resize-none rounded-[2rem] border border-[#F2E8E6] bg-[#FFFCFA] px-4 py-3.5 text-sm focus:border-[#2C2B2C] focus:outline-none" />
          </div>
          <button type="submit" disabled={loading} data-testid="contact-submit"
            className="mt-6 flex w-full items-center justify-center gap-2 bg-[#232222] rounded-full py-4 text-xs uppercase tracking-[0.18em] text-[#FFFCFA] transition-colors hover:bg-[#8A7F86] disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : done ? <Check className="h-4 w-4" /> : null}
            {done ? "Message Sent" : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}
